import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Assignment } from '@mui/icons-material';
import ZerpaTable, { ColumnObject, ZerpaTableStateSetters } from '../layout/table/ZerpaTable';
import {
  Enrollment,
  ZerpaState,
} from '../../redux/types';
import { EnrollmentsFull } from '../../loopback/apiClient/EnrollmentApi';
import { downloadFromContainer } from '../../utilities/urlHelper';
import { MANUALS_CONTAINER_NAME } from '../../environment';
import TrialDetails from './TrialDetails';
import { getAccreditedStatusId } from '../utilities/TrialUtility';

export type EnrollmentExteded = {
  id: number;
  status: Enrollment['status'];
  statusText: React.ReactNode;
  trialName: string;
  trialCode: string;
  trialManual: string;
  tag: JSX.Element[] | undefined;
  trialDeactivated: boolean;
  endYear: number;
  enroller: string;
  enrollment: EnrollmentsFull;
  trial: EnrollmentsFull['trial'];
}

export type ExtendedTrial = EnrollmentsFull['trial'] & {
  enrollment: EnrollmentsFull;
};

type EnrollmentListType = {
  enrollments: EnrollmentExteded[];
  showDeactivatedTrials: boolean;
  enrollmentType: 'institute' | 'individual';
  renderTrialActions: (enrollment: ExtendedTrial) => React.ReactNode;
  onZerpaTableStateChange?: (state: ZerpaState, setters?: ZerpaTableStateSetters) => void;
}

const tableColumns: ColumnObject[] = [
  {
    keyName: 'trialName',
    text: <FormattedMessage id="trials.trialName" />,
    sortable: true,
  },
  {
    keyName: 'tag',
    text: <FormattedMessage id="trials.tag" />,
    sortable: false,
  },
  {
    keyName: 'endYear',
    text: <FormattedMessage id="trials.trialYear" />,
    sortable: true,
  },
  {
    keyName: 'statusText',
    text: <FormattedMessage id="trials.enrollmentstatus" />,
    sortable: true,
  },
  {
    keyName: 'enroller',
    text: <FormattedMessage id="trials.enroller" />,
    sortable: true,
  },
  {
    keyName: 'isAccredited',
    text: <FormattedMessage id="trials.accreditedStatus" />,
  },
  {
    keyName: 'trialManual',
    text: <FormattedMessage id="trials.manual" />,
  },
];

const EnrollmentsList: React.FC<EnrollmentListType> = ({
  enrollments,
  showDeactivatedTrials,
  enrollmentType,
  renderTrialActions,
  onZerpaTableStateChange,
}: EnrollmentListType) => {
  const renderManualButton = (manual: string) => (
    manual
      ? (
        <Assignment
          onClick={(event) => {
            event.stopPropagation();
            downloadFromContainer(MANUALS_CONTAINER_NAME, manual);
          }}
        />
      )
      : <span />
  );

  const renderDetails = (enrollment: EnrollmentExteded) => (
    <TrialDetails
      trial={enrollment.trial}
      enrollment={enrollment.enrollment}
      renderTrialActions={renderTrialActions}
    />
  );

  const renderOnSelect = (key: unknown) => {
    const matchedEnrollment = enrollments.find((e) => e.id === key);
    return matchedEnrollment && renderDetails(matchedEnrollment);
  };

  let columns = tableColumns;
  if (enrollmentType === 'individual') {
    columns = tableColumns.filter((c) => c.keyName !== 'enroller');
  }

  let normalizedEnrollment = [...enrollments].filter((e) => (
    enrollmentType === 'individual' ? e.trial.isDRT : !e.trial.isDRT
  ));

  if (!showDeactivatedTrials) {
    normalizedEnrollment = normalizedEnrollment
      .filter((enrollment) => !enrollment.trialDeactivated);
  }

  const enrollmentsWithManual = normalizedEnrollment
    .map((enrollment) => ({
      ...enrollment,
      isAccredited: enrollment.trial.isAccredited === undefined || enrollment.trial.isAccredited === null ? '-' : <FormattedMessage id={getAccreditedStatusId(enrollment.trial.isAccredited)} />,
      trialManual: renderManualButton(enrollment.trialManual),
    }));

  return (
    <ZerpaTable
      columns={columns}
      keyIdentifier="id"
      tableRows={enrollmentsWithManual}
      renderOnSelected={renderOnSelect}
      getZerpaState={onZerpaTableStateChange}
      showPagination
      dense
      color={enrollmentType === 'individual' ? 'green' : undefined}
    />
  );
};

export default EnrollmentsList;
